import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CalendarEventService } from '@karve.it/features';
import { Store, createAction, props } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { take, map } from 'rxjs/operators';

import { BaseCalendarEventFragment, BulkEditCalendarEventGQL, BulkEditCalendarEventMutation, EditCalendarEventGQL, FullCalendarEventFragment } from '../../generated/graphql.generated';

import { JobEventStatus } from '../global.constants';
import { CalendarEvent } from '../interfaces/calendarEvents';
import { CompleteEventsConfirmationComponent } from '../shared/complete-events-confirmation/complete-events-confirmation.component';
import { currentTimeSeconds } from '../time';

import { DetailsHelperService } from './details-helper.service';
import { FreyaHelperService } from './freya-helper.service';
import { FreyaNotificationsService } from './freya-notifications.service';
import { ResponsiveHelperService } from './responsive-helper.service';

export const eventHelperUpdateEventSuccess = createAction(
  '[Event Helper] Update Event Success',
  props<{
    updatedEvent: BulkEditCalendarEventMutation['bulkEditCalendarEvent']['events'][number];
  }>(),
);

@Injectable({
  providedIn: 'root'
})
export class EventHelperService {

  constructor(
    private eventService: CalendarEventService,
    private detailsHelper: DetailsHelperService,
    private localNotify: FreyaNotificationsService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private editCalendarEventGQL: EditCalendarEventGQL,
    private freyaHelper: FreyaHelperService,
    private responsiveHelper: ResponsiveHelperService,
    private router: Router,
    private store: Store,
    private bulkEditCalendarEventGQL: BulkEditCalendarEventGQL,
  ) { }

  updateEventStatus(id: string, status: JobEventStatus){
    return new Promise((resolve, reject) => {
      this.eventService.editCalendarEvent({ids: [id], edit: {
        status
      }}).subscribe(() => {
        this.detailsHelper.pushUpdate({
          id,
          type:'Events',
          action:'update'
        });
        this.localNotify.success(`Event status set to ${status}`);
        resolve(true);
      }, (err) => {
        this.localNotify.apolloError(`Failed to update event status`,err);
        reject();
      });
    });
  }

  unscheduleEvent(event: FullCalendarEventFragment | CalendarEvent){
    this.confirmationService.confirm({
      header: 'Cancel Event?',
      message: `Cancelling this event will remove it from the schedule and remove any associated charges and discounts`,
      acceptLabel: 'Cancel this Event',
      acceptIcon: 'pi pi-ban',
      rejectLabel: `Don't Cancel this Event`,
      rejectIcon: 'pi pi-times',
      accept: () => {
          const updateEventInput = {
            id: event.id,
            edit: {
              status: 'cancelled',
            },
          };

          this.bulkEditCalendarEventGQL.mutate({ edits: [ updateEventInput ]}).pipe(
            take(1),
          ).subscribe({
            next: (res) => {
              this.localNotify.success('Event cancelled');
              this.detailsHelper.pushUpdate({
                id: event.id,
                type:'Events',
                action:'update',
                update: { status: 'cancelled' },
              });

              this.store.dispatch(eventHelperUpdateEventSuccess({
                updatedEvent: res.data.bulkEditCalendarEvent.events[0],
              }));
            },
            error: (err) => {
              this.localNotify.apolloError('Could not cancel event', err);
            }
          });
      }
    });
  }

  uncancelEvent(event: FullCalendarEventFragment, navigateCommands?: any[]) {

    const message = `This will add the event back into the schedule, `
    + 'but it will not actually book the event nor check if there are any conflicting events scheduled for the same time. '
    + 'Please make sure there are no conflicting events, then book the event manually.';

    this.confirmationService.confirm({
      header: 'Undo Cancel?',
      message,
      acceptLabel: 'Undo Cancel',
      acceptIcon: 'pi pi-undo',
      rejectLabel: 'Nevermind',
      rejectIcon: 'pi pi-times',
      accept: () => {
          const updateEventInput = {
            id: event.id,
            edit: {
              status: 'pending',
            },
          };

          this.bulkEditCalendarEventGQL.mutate({ edits: [ updateEventInput ]}).pipe(
            take(1),
          ).subscribe({
            next: (res) => {
              this.localNotify.success('Cancellation undone');

              this.detailsHelper.pushUpdate({
                id: event.id,
                type:'Events',
                action:'update',
              });

              this.store.dispatch(eventHelperUpdateEventSuccess({
                updatedEvent: res.data.bulkEditCalendarEvent.events[0],
              }));

              if (navigateCommands) {
                this.router.navigate(navigateCommands);
              }
            },
            error: (err) => {
              this.localNotify.apolloError('Could not undo cancel', err);
            }
          });
      }
    });
  }

  confirmCompleteEvents(events: BaseCalendarEventFragment[], prompt: string) {

    const incompleteEvents = this.getPastIncompleteEvents(events);

    if (!incompleteEvents.length) { return Promise.resolve({
      confirmed: false,
      incompleteEvents,
    }); }

    const ref = this.dialogService.open(CompleteEventsConfirmationComponent, {
      header: 'Mark Events as Complete?',
      contentStyle: this.freyaHelper.getDialogContentStyle('1.5rem'),
      width: this.responsiveHelper.dialogWidth,
      closable: false,
      data: {
        events: incompleteEvents,
        prompt,
      },
    });

    return ref.onClose.pipe(
      take(1),
      map((confirmed: boolean) => ({ confirmed, incompleteEvents }))
    ).toPromise();
  }

  getPastIncompleteEvents(events: FullCalendarEventFragment[]) {

    if (!events) {
      return [];
    }

    const now = currentTimeSeconds();

    return events.filter((e) => {

      const finished = e.start < now;

      const incomplete = e.status !== 'completed' && e.status !== 'required';

      return finished && incomplete;
    });
  }

  markConfirmed(events: BaseCalendarEventFragment[]) {

    if (!events?.length) { return; }

    const ids = events.map((e) => e.id);
    const edit = { status: 'confirmed' };
    return this.editCalendarEventGQL.mutate({ ids, edit }).subscribe(() => {
      this.detailsHelper.pushUpdate({
        id: ids[0],
        type:'Events',
        action:'update'
      });

      this.localNotify.success(`Event${ ids?.length > 1 ? 's' : '' } confirmed`);
    }, (err) => {
      this.localNotify.apolloError('Failed to mark events as confirmed', err);
    });

  }

  markComplete(events: BaseCalendarEventFragment[]) {

    if (!events?.length) { return; }

    const ids = events.map((e) => e.id);

    const edit = { status: 'completed' };

    return this.editCalendarEventGQL.mutate({ ids, edit }).subscribe(() => {
      this.detailsHelper.pushUpdate({
        id: ids[0],
        type:'Events',
        action:'update'
      });

      this.localNotify.success('Events marked as complete');
    }, (err) => {
      this.localNotify.apolloError('Failed to mark events as complete', err);
    });

  }

}
